// All eCourts calls go through Jurist Bot's own axiosInstance (adds the authToken header).
// Every call also sends the logged-in user's id (user?.id from useAuth, set by EcourtApp) as user_id.
// The eCourtsIndia key stays on the backend — never in React.
import axiosInstance from '../config/apiconfig';

const BASE = '/ecourt';
let currentUserId = null;

/** EcourtApp calls this with user?.id from useAuth(). */
export function setEcourtUser(id) {
  currentUserId = id == null ? null : String(id);
}

export class EcourtApiError extends Error {
  constructor(status, code, message, retryAfter) {
    super(message);
    this.status = status;
    this.code = code;
    this.retryAfter = retryAfter;
  }
}

function clean(params) {
  if (!params) return undefined;
  const out = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) {
      const arr = v.filter((x) => x !== undefined && x !== null && x !== '');
      if (arr.length) out[k] = arr;
    } else {
      out[k] = v;
    }
  });
  return out;
}

async function toError(err) {
  if (!err.response) {
    const slow = err.code === 'ECONNABORTED';
    return new EcourtApiError(0, slow ? 'TIMEOUT' : 'NETWORK', slow ? 'This is taking too long. Please try again.' : 'Cannot reach the server. Check your connection.');
  }
  const { status, headers } = err.response;
  let { data } = err.response;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try { data = JSON.parse(await data.text()); } catch (_) { data = {}; }
  }
  const e = (data && data.error) || {};
  if (status === 401) return new EcourtApiError(401, 'UNAUTHENTICATED', 'Your session has expired. Please log in again.');
  return new EcourtApiError(status, e.code || `HTTP_${status}`, e.message || 'Request failed', headers && headers['retry-after']);
}

async function call(method, url, { params, data, timeout, responseType } = {}) {
  if (!currentUserId) throw new EcourtApiError(401, 'UNAUTHENTICATED', 'Your session has expired. Please log in again.');
  const withBody = method === 'post' || method === 'patch';
  let res;
  try {
    res = await axiosInstance.request({
      method,
      url: BASE + url,
      params: clean({ ...(params || {}), ...(withBody ? {} : { user_id: currentUserId }) }),
      data: withBody ? { ...(data || {}), user_id: currentUserId } : undefined,
      timeout: timeout || 60000,
      responseType,
      paramsSerializer: { indexes: null }, // arrays as ?litigants=A&litigants=B
    });
  } catch (err) {
    throw await toError(err);
  }
  // Never treat an error reply as data (some interceptors hand errors back as normal responses).
  if (res && res.status >= 400) throw await toError({ response: res });
  return res;
}

const get = (url, params, timeout) => call('get', url, { params, timeout }).then((r) => r.data);
const post = (url, data, timeout) => call('post', url, { data, timeout }).then((r) => r.data);

export const ecourtApi = {
  // Account
  usage: () => get('/usage'),
  charges: (limit = 50) => get('/charges', { limit }),

  // Reference (free)
  capabilities: () => get('/search/capabilities'),
  enums: (types) => get('/enums', { types }),

  // Cases & orders
  search: (params) => get('/search', params),
  caseDetail: (cnr, fresh) => get(`/cases/${cnr}`, { fresh: fresh ? '1' : '' }),
  provisions: (cnr) => get(`/cases/${cnr}/provisions`),
  // returns the axios response: status 200 = ready, 202 = AI still preparing (retry)
  orderAi: (cnr, file) => call('get', `/cases/${cnr}/orders/${encodeURIComponent(file)}/ai`, { timeout: 90000 }),
  orderMarkdown: (cnr, file) => get(`/cases/${cnr}/orders/${encodeURIComponent(file)}/markdown`, null, 310000),
  orderPdf: async (cnr, file, signed) => {
    const res = await call('get', `/cases/${cnr}/orders/${encodeURIComponent(file)}/pdf`, {
      params: { signed: String(signed) }, responseType: 'blob', timeout: 310000,
    });
    const type = (res.data && res.data.type) || '';
    if (/json/i.test(type)) throw await toError({ response: { ...res, status: res.status >= 400 ? res.status : 502 } });
    const disposition = res.headers['content-disposition'] || '';
    const name = (disposition.match(/filename="([^"]+)"/) || [])[1] || file;
    return { blob: res.data, name, charged: res.headers['x-ec-charged'] || '' };
  },

  // Refresh
  refresh: (cnr) => post(`/cases/${cnr}/refresh`),
  bulkRefresh: (cnrs) => post('/cases/bulk-refresh', { cnrs }),
  bulkRefreshStatus: (cnrs) => post('/cases/bulk-refresh-status', { cnrs }),

  // Cause list
  states: () => get('/causelist/court-structure/states'),
  districts: (s) => get(`/causelist/court-structure/states/${encodeURIComponent(s)}/districts`),
  complexes: (s, d) => get(`/causelist/court-structure/states/${encodeURIComponent(s)}/districts/${encodeURIComponent(d)}/complexes`),
  courts: (s, d, c) => get(`/causelist/court-structure/states/${encodeURIComponent(s)}/districts/${encodeURIComponent(d)}/complexes/${encodeURIComponent(c)}/courts`),
  availableDates: (params) => get('/causelist/available-dates', params),
  causeSearch: (params) => get('/causelist/search', params),
  cnrBatch: (cnrs) => post('/causelist/cnr-batch', { cnrs }),

  // Legal check
  lcModels: () => get('/legal-check/models'),
  lcSubmit: (body) => post('/legal-check', body),
  lcStatus: (code) => get(`/legal-check/${code}`),
  lcReport: (code, params) => get(`/legal-check/${code}/report`, params),
  lcLog: (code) => get(`/legal-check/${code}/log`),
  lcList: () => get('/legal-check'),

  // Electoral (identity check, consent required)
  epicVerify: (body) => post('/electoral/epic/verify', body),
  electoralSearch: (body) => post('/electoral/search', body),

  // Acts & Sections
  actsStatus: () => get('/acts/status'),
  actSection: (key, n) => get('/acts/section', { key, n }),
  sectionEquivalents: (key, n) => get('/acts/equivalents', { key, n }),
  resolveAct: (name) => get('/acts/resolve', { name }),
  searchSections: (q) => get('/acts/search', { q }),
  sectionsBatch: (items) => post('/acts/sections/batch', { items }),

  // My Cases
  myCases: () => get('/my-cases'),
  addMyCase: (body) => post('/my-cases', body),
  checkMyCase: (cnr) => get(`/my-cases/check/${cnr}`),
  updateMyCase: (id, body) => call('patch', `/my-cases/${id}`, { data: body }).then((r) => r.data),
  removeMyCase: (id) => call('delete', `/my-cases/${id}`).then((r) => r.data),
  myCaseUpdates: (cnr) => get('/my-cases/updates', { cnr }),
  markSeen: (id) => post('/my-cases/seen', id ? { id } : {}),
  syncMyCases: (ids) => post('/my-cases/sync', ids ? { ids } : {}),
};